/** Portable competition data. Definitions remain in the administrator schema. */
export interface CustomDisciplineValueSnapshot {
  fieldDefinitionId: string;
  textValue?: string | null;
  numberValue?: number | string | { toString(): string } | null;
  selectOptionId?: string | null;
}

export interface CustomDisciplineGroupSnapshot {
  id: string;
  groupTypeId: string;
  name?: string | null;
  values: CustomDisciplineValueSnapshot[];
  athletes: {
    id: string;
    order: number;
    values: CustomDisciplineValueSnapshot[];
  }[];
}

/** Optional lifecycle marker. Its value is written by administrator Behavior. */
export function customDisciplineGroupIsOver(value: unknown): boolean {
  const group = value as {
    values?: CustomDisciplineValueSnapshot[];
    groupType?: { fields?: { id?: string; key: string; scope: string }[] };
  } | null;
  const marker = group?.groupType?.fields?.find(
    (field) => field.scope === "GROUP" && field.key === "isOver",
  );
  return Boolean(
    marker?.id &&
      group?.values?.some(
        (item) =>
          item.fieldDefinitionId === marker.id && item.textValue === "true",
      ),
  );
}

/** Only data columns cross the local/remote boundary, never nested Prisma relations. */
export function normalizeCustomDisciplineGroupSnapshot(
  value: CustomDisciplineGroupSnapshot,
): CustomDisciplineGroupSnapshot {
  if (!value?.id || !value.groupTypeId || !Array.isArray(value.athletes))
    throw new Error("Données du groupe personnalisé incomplètes");
  const values = (items: CustomDisciplineValueSnapshot[]) => {
    const seen = new Set<string>();
    return (items ?? []).map((item) => {
      if (!item.fieldDefinitionId || seen.has(item.fieldDefinitionId))
        throw new Error("Champ personnalisé absent ou dupliqué");
      seen.add(item.fieldDefinitionId);
      const numberValue =
        item.numberValue == null ? null : Number(item.numberValue);
      if (numberValue !== null && !Number.isFinite(numberValue))
        throw new Error("Valeur numérique personnalisée invalide");
      return {
        fieldDefinitionId: item.fieldDefinitionId,
        textValue: item.textValue ?? null,
        numberValue,
        selectOptionId: item.selectOptionId ?? null,
      };
    });
  };
  const athleteIds = new Set<string>();
  return {
    id: value.id,
    groupTypeId: value.groupTypeId,
    name: value.name ?? null,
    values: values(value.values),
    athletes: value.athletes.map((athlete) => {
      if (!athlete.id || athleteIds.has(athlete.id))
        throw new Error("Athlète personnalisé absent ou dupliqué");
      athleteIds.add(athlete.id);
      return {
        id: athlete.id,
        order: Number.isSafeInteger(athlete.order) ? athlete.order : 0,
        values: values(athlete.values),
      };
    }),
  };
}

/** Shared by SQLite and MySQL; call inside the host's transaction. */
export async function persistCustomDisciplineGroupSnapshot(
  tx: any,
  input: CustomDisciplineGroupSnapshot,
): Promise<CustomDisciplineGroupSnapshot> {
  const snapshot = normalizeCustomDisciplineGroupSnapshot(input);
  const definitions = await tx.customDisciplineFieldDefinition.findMany({
    where: { groupTypeId: snapshot.groupTypeId },
    select: { id: true, scope: true, options: { select: { id: true } } },
  });
  const validateValues = (
    values: CustomDisciplineValueSnapshot[],
    scope: string,
  ) => {
    for (const value of values) {
      const field = definitions.find(
        (item: any) => item.id === value.fieldDefinitionId,
      );
      if (!field || field.scope !== scope)
        throw new Error(
          "Le schéma administrateur du groupe doit être synchronisé",
        );
      if (
        value.selectOptionId &&
        !field.options.some((option: any) => option.id === value.selectOptionId)
      )
        throw new Error(
          "Option personnalisée inconnue dans le schéma administrateur",
        );
    }
  };
  validateValues(snapshot.values, "GROUP");
  snapshot.athletes.forEach((athlete) =>
    validateValues(athlete.values, "ATHLETE"),
  );
  const collisions = await tx.customDisciplineAthlete.findMany({
    where: {
      id: { in: snapshot.athletes.map((athlete) => athlete.id) },
      groupId: { not: snapshot.id },
    },
    select: { id: true },
  });
  if (collisions.length)
    throw new Error("Un athlète importé appartient déjà à un autre groupe");
  const existing = await tx.customDisciplineGroup.findUnique({
    where: { id: snapshot.id },
  });
  if (existing && existing.groupTypeId !== snapshot.groupTypeId)
    throw new Error(
      "Le type du groupe personnalisé ne correspond pas au groupe existant",
    );
  await tx.customDisciplineGroup.upsert({
    where: { id: snapshot.id },
    create: {
      id: snapshot.id,
      groupTypeId: snapshot.groupTypeId,
      name: snapshot.name,
    },
    update: { name: snapshot.name },
  });
  await tx.customDisciplineGroupValue.deleteMany({
    where: { groupId: snapshot.id },
  });
  if (snapshot.values.length)
    await tx.customDisciplineGroupValue.createMany({
      data: snapshot.values.map((value) => ({
        ...value,
        groupId: snapshot.id,
      })),
    });
  await tx.customDisciplineAthlete.deleteMany({
    where: {
      groupId: snapshot.id,
      id: { notIn: snapshot.athletes.map((athlete) => athlete.id) },
    },
  });
  for (const athlete of snapshot.athletes) {
    await tx.customDisciplineAthlete.upsert({
      where: { id: athlete.id },
      create: { id: athlete.id, groupId: snapshot.id, order: athlete.order },
      update: { order: athlete.order },
    });
    await tx.customDisciplineAthleteValue.deleteMany({
      where: { athleteId: athlete.id },
    });
    if (athlete.values.length)
      await tx.customDisciplineAthleteValue.createMany({
        data: athlete.values.map((value) => ({
          ...value,
          athleteId: athlete.id,
        })),
      });
  }
  return snapshot;
}
